import { Map } from "immutable";
import { z } from "zod/v4";

import { type Tuple } from "../Tuple";
import {
	type Arrangement,
	ZSet,
	dehydrateArrangement,
	hydrateArrangement,
	rawArrangementSchema,
} from "../ZSet";
import { type BilinearOperatorLogic } from "./BilinearOperatorLogic";
import {
	type JsonPathRef,
	type SubqueryRef,
	jsonpathRefSchema,
	subqueryRefSchema,
} from "./schema-util";

export function join<L, R, Out>(
	spec: JoinSpec,
	makeOutput: (l: Tuple<L>, r: Tuple<R>) => Tuple<Out>
): BilinearOperatorLogic<L, R, Out, JoinState<L, R>> {
	const kL = (t: Tuple<L>) => t.pluck(spec.leftKey.path);
	const kR = (t: Tuple<R>) => t.pluck(spec.rightKey.path);

	return (
		leftΔ: ZSet<L>,
		rightΔ: ZSet<R>,
		state: JoinState<L, R> = { type: "join", leftByK: Map(), rightByK: Map() }
	): [ZSet<Out>, JoinState<L, R>] => {
		// Some implementation notes. This join is an inner equi-join over ℤ-sets. In the relational
		// algebra, for two tables L and R, we would say L ⋈ R.
		//
		// In general, the goal of `differential` is to decompose complicated SQL queries into a
		// tree of transformations that (1) look vaguely "linear", and (2) work for both the batch
		// and streaming case. By "linear" we just mean "has all the nice properties of addition,
		// like associativity, commutativity, and so on."
		//
		// `JOIN`, being a so-called "bilinear operator", is one of the more challenging cases we
		// will have to deal with. We will see that it too can be decomposed into a few linear
		// operations, and with a bit of extra memory, it can compute both the batch and streaming
		// cases relatively efficiently.
		//
		// Since `differential` operates entirely on ℤ-sets, we will use the well-known "bilinear
		// decomposition" of `JOIN` into three linear operations over changesets, represented here
		// with the symbol Δ. But do note that this can easily represent the batch case as well,
		// since running the query with existing data is the same thing as assigning all records in
		// a table stored on disk a weight of 1, and passing them flatly to the `JOIN` operator.
		//
		// The three linear operations, stated slightly more formally, are as follows:
		//
		//     Δ(L ⋈ R) =                       // changes produced by a join on L and R
		//                 (ΔL ⋈  R)            // new left rows vs. old right rows
		//               + ( L ⋈ ΔR)            // new right rows vs. old left rows
		//               + (ΔL ⋈ ΔR)            // rows new on both inputs this tick
		//     weight(out) = weightL * weightR
		//
		// One way of thinking about this is as 3 completely independent computations that can occur
		// in any order, or in parallel, or whatever. They all operate on the previous state, and
		// none of them require that they talk to the other. You COULD implement step 3
		// incrementally as part of step 2 but in pedogagical code this is uncommon because it
		// greatly complicates the implementation.
		//
		// Thus, below, you will see that the update algorithm per epoch will be:
		//
		//     1. compute ΔL ⋈ S    (for-each leftΔ, probe rightByK)
		//     2. compute R ⋈ ΔS    (for-each rightΔ, probe leftByK)
		//     3. compute ΔL ⋈ ΔS either when key appears in both deltas, or all at once as a
		//        standalone step
		//     4. integrate deltas into state:
		//           leftByK  ⊕= leftΔ
		//           rightByK ⊕= rightΔ
		//
		// Note that in the code below, steps 1–3 MUST read EXCLUSIVELY from the PREVIOUS STATE;
		// integration happens after all joins so we don’t double‑count.
		//
		// Time complexity:
		//
		//     time = O( |ΔL|·|S_k| + |ΔS|·|R_k| + |ΔL∩ΔS|·|ΔL_k|·|ΔS_k| )
		//              where *_k means bucket size for one key.
		//     mem  = O( distinctKeys(R) + distinctKeys(S) + Δ batch size )
		//
		// Caveats / TODO
		//
		//     – Assumes equi‑join; for non‑equi conditions we’d need a
		//       secondary index or nested‑loop fallback.
		//     – Outer joins: add synthetic “NULL row” with weight 1 when a
		//       bucket becomes empty.
		//     – For very large buckets consider moving from immutable ZSet
		//       to a custom indexed multiset for better perf.
		//

		let out = ZSet.empty<Out>();

		const newState = {
			type: "join",
			leftByK: state.leftByK,
			rightByK: state.rightByK,
		} satisfies JoinState<L, R>;

		/* --- 1. ΔL ⋈ R --- */
		for (const l of leftΔ) {
			const keyId = kL(l.tuple).contentId();
			const rightBag = state.rightByK.get(keyId, ZSet.empty<R>());
			for (const r of rightBag) {
				out = out.add({ tuple: makeOutput(l.tuple, r.tuple), weight: l.weight * r.weight });
			}
		}

		/* --- 2. L ⋈ ΔR --- */
		for (const r of rightΔ) {
			const keyId = kR(r.tuple).contentId();
			const leftBag = state.leftByK.get(keyId, ZSet.empty<L>());
			for (const l of leftBag) {
				out = out.add({ tuple: makeOutput(l.tuple, r.tuple), weight: l.weight * r.weight });
			}
		}

		/* --- 3. ΔL ⋈ ΔR (optional fast‑path if both non‑empty) --- */
		// Build a hash‑bucket view of the right delta so we can probe it in O(1) per key that
		// exists on the left side.  Because delta batches are usually small (<< integrated state)
		// this is cheap.
		const rightBuckets = Map<string, ZSet<R>>().withMutations((b) => {
			for (const rec of rightΔ) {
				const keyId = kR(rec.tuple).contentId();
				const bag = b.get(keyId, ZSet.empty<R>());
				b.set(keyId, bag.add(rec)); // Z-set addition inside bucket
			}
		});

		/* Iterate over leftΔ and check if its key also exists in rightBuckets. */
		for (const l of leftΔ) {
			const keyId = kL(l.tuple).contentId();
			const bucketR = rightBuckets.get(keyId);
			if (!bucketR) {
				continue;
			} // key not present in ΔR → skip

			for (const r of bucketR) {
				out = out.add({
					tuple: makeOutput(l.tuple, r.tuple),
					weight: l.weight * r.weight, // weight product rule
				});
			}
		}
		/* --- 4. integrate state --- */
		newState.leftByK = zsetAddToMap(state.leftByK, leftΔ, kL);
		newState.rightByK = zsetAddToMap(state.rightByK, rightΔ, kR);

		return [out, newState];
	};
}

function zsetAddToMap<Row, K>(
	map: Map<string, ZSet<Row>>,
	delta: ZSet<Row>,
	kFn: (t: Tuple<Row>) => Tuple<K>
) {
	for (const ztuple of delta) {
		const kId = kFn(ztuple.tuple).contentId();
		const bag = map.get(kId, ZSet.empty<Row>()).add(ztuple); // add = weight±

		if (bag.size === 0) {
			map = map.remove(kId);
		} else {
			map = map.set(kId, bag);
		}
	}

	return map;
}

//
// Enriched Join State.
//

export type JoinState<L, R> = {
	type: "join";
	leftByK: Arrangement<L>; // Map<keyId, ZSet<L>>
	rightByK: Arrangement<R>;
};

export const emptyJoinState = <L, R>(): JoinState<L, R> => ({
	type: "join",
	leftByK: Map(),
	rightByK: Map(),
});

export const hydrateJoinState = <L, R>(state: RawJoinState): JoinState<L, R> => ({
	type: "join",
	leftByK: hydrateArrangement(state.leftByK),
	rightByK: hydrateArrangement(state.rightByK),
});

export const dehydrateJoinState = <L, R>(state: JoinState<L, R>): RawJoinState => ({
	type: "join",
	leftByK: dehydrateArrangement(state.leftByK),
	rightByK: dehydrateArrangement(state.rightByK),
});

//
// Raw Join State.
//

export const joinSpecSchema = z.object({
	type: z.literal("join"),
	leftKey: jsonpathRefSchema,
	rightKey: jsonpathRefSchema, // allows α≠β
	right: subqueryRefSchema,
});
export type JoinSpec = z.infer<typeof joinSpecSchema>;

export function joinSpec(
	leftKey: JsonPathRef,
	rightKey: JsonPathRef,
	right: SubqueryRef
): JoinSpec {
	return {
		type: "join",
		leftKey,
		rightKey,
		right,
	};
}

export const rawJoinStateSchema = z.object({
	type: z.literal("join"),
	leftByK: rawArrangementSchema,
	rightByK: rawArrangementSchema,
});
export type RawJoinState = z.infer<typeof rawJoinStateSchema>;
